"use client";

import * as React from "react";
import { useMemo } from "react";

import Link from "next/link";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { LinkButton } from "@stadium/shared/src/components/shared/button";
import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import ExternalButton from "@stadium/ui/src/components/button/external-button";
import Newsletter from "@components/swagmagic/footer/news-letter";

import { CATALOG_HOME_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";

import {
  FooterColumn,
  FooterLink,
  SocialLink,
  LogoData,
  FOOTER_DEFAULTS,
  WATERMARK_URLS,
  hasColumnData,
} from "@components/swagmagic/footer/constants";

import styles from "./main.module.scss";

// Types
interface NewsletterData {
  newsletterTitle?: string;
  newsletterPlaceholder?: string;
  newsletterButtonAriaLabel?: string;
}

interface CTAButtonData {
  ctaText?: string;
  ctaUrl?: string;
}

interface WaterMark1920 {
  url: string;
}
interface FooterData {
  logo?: LogoData;
  newsLetter?: NewsletterData;
  footerCta?: CTAButtonData;
  column1?: FooterColumn;
  column2?: FooterColumn;
  column3?: FooterColumn;
  socialLinks?: SocialLink[];
  copyrightText?: string;
  legalLinks?: FooterLink[];
  watermark1920Image?: WaterMark1920;
}

export interface FooterProps {
  footerData?: FooterData;
  className?: string;
}

const renderColumn = (column: FooterColumn) => (
  <div className={styles.column}>
    <h3 className={styles.columnTitle}>{column.title}</h3>
    <ul className={styles.columnLinks}>
      {column.links
        .filter((link) => link.text && (link.url || link.linkType || link.action))
        .map((link) => (
          <li key={`${link.text}-${link.url}-${link.linkType}-${link.action}`}>
            {link.action ? (
              <ExternalButton
                btnStyle="link"
                href="#"
                className={styles.columnLink}
                target="_self"
                onClick={(e) => {
                  e.preventDefault();
                  if (link.action === "open_cookie_preferences") {
                    globalThis?.Osano?.cm?.showDrawer("osano-cm-dom-info-dialog-open");
                    return;
                  }
                  if (link.action === "open_help_messenger") {
                    globalThis?.zE?.("messenger", "open");
                  }
                }}
              >
                {link.text}
              </ExternalButton>
            ) : (
              <LinkWithLang
                type={link.linkType || CATALOG_HOME_LINK_TYPE}
                data={link}
                className={styles.columnLink}
                target={link.openInNewTab ? "_blank" : "_self"}
                showExternalLinkIcon={link.openInNewTab}
              >
                {link.text}
              </LinkWithLang>
            )}
          </li>
        ))}
    </ul>
  </div>
);

const emptyObj: Record<string, any> = {};

function LegalLinkNoUrl({ link, className }: Readonly<{ link: FooterLink; className: string }>) {
  const noUrl = link.noUrl;
  const dataKey = noUrl?.dataAttribute?.key;
  const dataValue = noUrl?.dataAttribute?.value;
  const dataProps =
    dataKey != null && dataKey !== ""
      ? ({ [dataKey]: dataValue ?? "" } as React.HTMLAttributes<HTMLAnchorElement>)
      : undefined;

  return (
    <ExternalButton
      btnStyle="link"
      href=""
      target={noUrl?.target ?? "_self"}
      className={className}
      {...dataProps}
      onClick={
        noUrl?.preventDefaultClick
          ? (e) => {
              e.preventDefault();
            }
          : undefined
      }
    >
      {link.text}
    </ExternalButton>
  );
}

const renderLegalLinks = ({
  validLegalLinks,
  className,
  linkClassName,
}: {
  validLegalLinks: FooterLink[];
  className: string;
  linkClassName: string;
}) => {
  return (
    <div className={className}>
      {validLegalLinks.map((link) =>
        link?.url ? (
          <Link key={link.url} href={link.url} className={linkClassName}>
            {link.text}
          </Link>
        ) : (
          <LegalLinkNoUrl
            key={link.noUrl?.dataAttribute?.key ?? link.text}
            link={link}
            className={linkClassName}
          />
        ),
      )}
    </div>
  );
};

// Main Footer Component
const Footer = ({ footerData, className }: Readonly<FooterProps>) => {
  const {
    logo,
    newsLetter,
    footerCta,
    column1,
    column2,
    column3,
    socialLinks,
    copyrightText,
    legalLinks,
    watermark1920Image,
  } = footerData ?? FOOTER_DEFAULTS;

  const newsLetterData = newsLetter ?? emptyObj;

  const { isMobileDevice, isTabletDevice } = React.useContext(CurrentDeviceContext);
  const isMobile = isMobileDevice || isTabletDevice;

  const copyrightTextWithYear = `${copyrightText} ${new Date().getFullYear()}`;

  const footerCtaButton = footerCta?.ctaText && (
    <>
      {footerCta?.ctaUrl ? (
        <LinkButton styleType="no-style" href={footerCta?.ctaUrl} className={styles.primaryBtn}>
          {footerCta?.ctaText}
        </LinkButton>
      ) : (
        <LinkWithLang type={CATALOG_HOME_LINK_TYPE} data={footerCta} className={styles.primaryBtn}>
          {footerCta?.ctaText}
        </LinkWithLang>
      )}
    </>
  );
  const validLegalLinks = legalLinks?.filter((link) => link.text && (link.url || link.noUrl)) || [];
  const showLegalLinks = validLegalLinks.length > 0;

  const watermarkUrl = WATERMARK_URLS.desktop;

  const validSocialLinks =
    socialLinks?.filter((social) => social.url && social.iconUrl && social.platform) || [];

  const socialLinksContent =
    validSocialLinks.length > 0 ? (
      <>
        {validSocialLinks.map((social) => (
          <Link
            key={social.platform}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={social.platform}
            className={styles.socialLink}
          >
            <Multimedia
              type="image"
              src={social.iconUrl}
              alt={social.platform}
              className={styles.socialLinkImg}
            />
          </Link>
        ))}
      </>
    ) : null;

  const column1Content = useMemo(
    () => (column1 && hasColumnData(column1) ? renderColumn(column1) : null),
    [column1],
  );
  const column2Content = useMemo(
    () => (column2 && hasColumnData(column2) ? renderColumn(column2) : null),
    [column2],
  );
  const column3Content = useMemo(
    () => (column3 && hasColumnData(column3) ? renderColumn(column3) : null),
    [column3],
  );

  return (
    <footer className={`${styles.footer} ${className || ""}`}>
      {isMobile && (
        <div className={styles.mobileLayout}>
          {/* Logo and Newsletter Section */}
          <div className={styles.logoNewsletterSection}>
            <div className={styles.logoNewsletterContent}>
              <div className={styles.logoRow}>
                <Link href={logo?.logoUrl || ""}>
                  {logo?.logo?.url && (
                    <div className={styles.logoWrapper}>
                      <Multimedia
                        type="image"
                        src={logo.logo.url}
                        alt={logo.logoAlt}
                        className={styles.logoImage}
                      />
                    </div>
                  )}
                  <div className={styles.logoTextWrapper}>
                    <p className={styles.logoTextBy}>by</p>
                    {logo?.stadiumLogo?.url && (
                      <Multimedia
                        type="image"
                        src={logo.stadiumLogo.url}
                        alt={logo.logoAlt}
                        className={styles.stadiumLogoImage}
                      />
                    )}
                  </div>
                </Link>
                {footerCtaButton && <div className={styles.ctaButtonMd}>{footerCtaButton}</div>}
              </div>

              <Newsletter
                title={newsLetterData.newsletterTitle}
                placeholder={newsLetterData.newsletterPlaceholder}
                buttonAriaLabel={newsLetterData.newsletterButtonAriaLabel}
                inputWrapperClassName={styles.newsletterInputWrapper}
                containerClassName={styles.newsletter}
              />
            </div>
            {footerCtaButton && <div className={styles.ctaButtonMobile}>{footerCtaButton}</div>}
          </div>

          {/* Navigation Links */}
          <div className={styles.navigationLinks}>
            {(column1Content || column2Content) && (
              <div className={styles.columnsPair}>
                {column1Content}
                {column2Content}
              </div>
            )}
            {column3Content}
          </div>
          <div className={styles.footerBottomSection}>
            {/* Social Media Icons */}
            {socialLinksContent && <div className={styles.socialLinks}>{socialLinksContent}</div>}

            {/* Copyright and Legal Links */}
            <div className={styles.footerBottom}>
              <p className={styles.copyright}>{copyrightTextWithYear}</p>
              {showLegalLinks &&
                renderLegalLinks({
                  validLegalLinks,
                  className: styles.legalLinks,
                  linkClassName: styles.legalLink,
                })}
            </div>
          </div>
        </div>
      )}

      {!isMobile && (
        <div className={styles.desktopLayout}>
          <div className={styles.desktopContainer}>
            <div className={styles.desktopMainSection}>
              {/* Left Section */}
              <Link href={logo?.logoUrl || ""} className={styles.desktopLeftSection}>
                {logo?.logo?.url && (
                  <div className={styles.desktopLogoImage}>
                    <Multimedia
                      type="image"
                      src={logo.logo.url}
                      alt={logo.logoAlt}
                      className={styles.dkLogoImage}
                    />
                  </div>
                )}
                <div className={styles.logoTextWrapper}>
                  <p className={styles.logoTextBy}>by</p>
                  {logo?.stadiumLogo?.url && (
                    <Multimedia
                      type="image"
                      src={logo.stadiumLogo.url}
                      alt={logo.logoAlt}
                      className={styles.stadiumLogoImage}
                    />
                  )}
                </div>
              </Link>

              {/* Newsletter Section */}
              <div className={styles.desktopNewsletterWrapper}>
                <Newsletter
                  title={newsLetterData.newsletterTitle}
                  placeholder={newsLetterData.newsletterPlaceholder}
                  buttonAriaLabel={newsLetterData.newsletterButtonAriaLabel}
                  inputWrapperClassName={styles.desktopNewsletterInputWrapper}
                  containerClassName={styles.desktopNewsletter}
                />
              </div>

              {/* Right Section */}
              <div className={styles.desktopRightSection}>
                {footerCtaButton && (
                  <div className={styles.desktopCtaWrapper}>{footerCtaButton}</div>
                )}

                {(column1Content || column2Content || column3Content) && (
                  <div className={styles.desktopNavigationColumns}>
                    {column1Content}
                    {column2Content}
                    {column3Content}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.desktopFooterBottom}>
              {socialLinksContent && (
                <div className={styles.desktopSocialLinks}>{socialLinksContent}</div>
              )}
              <p className={styles.copyright}>{copyrightTextWithYear}</p>
              {showLegalLinks &&
                renderLegalLinks({
                  validLegalLinks,
                  className: styles.desktopLegalLinks,
                  linkClassName: styles.desktopLegalLink,
                })}
            </div>
          </div>
        </div>
      )}

      <div className={styles.backgroundDecoration}>
        <Multimedia
          type="image"
          src={watermarkUrl}
          alt="Swagmagic Watermark"
          className={styles.image}
        />
        {watermark1920Image?.url && (
          <Multimedia
            type="image"
            src={watermark1920Image?.url}
            alt="Swagmagic Watermark"
            className={styles.image1920}
          />
        )}
      </div>
    </footer>
  );
};

export default Footer;
